import Modal from '@/components/modal'
import { Button } from '@/components/ui/button'
import { endPoint } from '@/constant/end-point'
import { useDataFetch } from '@/hooks/use-data-fetch'
import usePagination from '@/hooks/use-pagination'
import useSorting from '@/hooks/use-sorting'
import { useUserPreference } from '@/hooks/use-user-preference'
import type { IHelpfulTip } from '@/interface/helpful-tops.interface'
import { Clock, Lightbulb, SkipForward, X } from 'lucide-react'
import { useEffect, useState } from 'react'

// Utility function to convert JSX-like syntax to proper HTML
const convertJSXToHTML = (jsxString: string): string => {
  if (!jsxString) return ''

  const converted = jsxString
    .replace(/\{\s*' '\s*\}/g, ' ') // Replace  with a space
    .replace(/\{\s*" "\s*\}/g, ' ') // Replace {" "} with a space
    .replace(/\{\s*` `\s*\}/g, ' ') // Replace {` `} with a space
    .replace(/\{\s*'\\s*'\s*\}/g, ' ') // Replace  with a space (escaped)
    .replace(/\{\s*"\\s*"\s*\}/g, ' ') // Replace {" "} with a space (escaped)
    .trim()

  return converted
}

export const TipsModal = () => {
  const [sorting] = useSorting()
  const [pagination, setPagination] = usePagination()
  const [isVisible, setIsVisible] = useState(false)
  const [currentTipIndex, setCurrentTipIndex] = useState(0)
  const [userChoice, setUserChoice] = useState<string | null>(null)
  const [tips, setTips] = useState<IHelpfulTip[]>([])
  const { userPreferences, upsertUserPreference } = useUserPreference()

  const { isLoading, values } = useDataFetch<IHelpfulTip>({
    endpoint: endPoint.helpfulTips,
    sorting,
    pagination: { pagination, setPagination },
  })

  // Check if user tips modal is enabled
  const isUserTipsModalEnabled =
    userPreferences?.find((preference) => preference.key === 'user-tips-modal')?.value !== 'off'

  useEffect(() => {
    if (values) {
      setTips(values)
      setCurrentTipIndex(0)
    }
  }, [values])

  useEffect(() => {
    // Don't show if user has disabled the tips modal
    if (!isUserTipsModalEnabled) {
      setUserChoice('disabled')
      return
    }

    const choice = localStorage.getItem('tips-user-choice')
    const remindDate = localStorage.getItem('tips-remind-date')

    // Check if it's time to remind user
    if (choice === 'remind' && remindDate) {
      const remindTime = new Date(remindDate)
      const now = new Date()

      if (now >= remindTime) {
        // 24 hours have passed, show tips again
        localStorage.removeItem('tips-user-choice')
        localStorage.removeItem('tips-remind-date')
        setUserChoice(null)
      } else {
        // Still waiting, don't show
        setUserChoice('remind')
        return
      }
    }

    // Show tips if no choice or if remind time has passed
    if (!choice) {
      const timer = setTimeout(() => setIsVisible(true), 2000)
      return () => clearTimeout(timer)
    }

    setUserChoice(choice)
  }, [isUserTipsModalEnabled])

  const handleSkip = async () => {
    localStorage.setItem('tips-user-choice', 'skip')
    setUserChoice('skip')
    await upsertUserPreference('user-tips-modal', 'off', true)
    setIsVisible(false)
  }

  const handleRemindLater = () => {
    // Set reminder for 24 hours from now
    const remindDate = new Date()
    remindDate.setHours(remindDate.getHours() + 24)

    localStorage.setItem('tips-remind-date', remindDate.toISOString())
    localStorage.setItem('tips-user-choice', 'remind')
    setUserChoice('remind')
    setIsVisible(false)
  }

  const handleNextTip = async () => {
    if (currentTipIndex < tips.length - 1) {
      setCurrentTipIndex((prev) => prev + 1)
    } else {
      localStorage.setItem('tips-user-choice', 'completed')
      await upsertUserPreference('user-tips-modal', 'off', true)
      setUserChoice('completed')
      setIsVisible(false)
    }
  }

  const handlePreviousTip = () => {
    if (currentTipIndex > 0) setCurrentTipIndex((prev) => prev - 1)
  }

  // Don't show if user has made a choice (except for remind which is handled above)
  if (userChoice || !isVisible) return null

  const currentTip = tips[currentTipIndex]
  const progress = ((currentTipIndex + 1) / tips.length) * 100

  if (!isLoading && tips.length === 0) return null

  return (
    <Modal open={isVisible} setOpen={setIsVisible}>
      {/* Header */}
      <div className='border-b pb-2'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center'>
              <Lightbulb className='w-5 h-5 text-yellow-600' />
            </div>
            <div>
              <h2 className='text-xl font-bold '>Helpful Tips</h2>
              <p className='text-sm'>
                Tip {currentTipIndex + 1} of {tips.length}
              </p>
            </div>
          </div>
          <Button
            variant='ghost'
            size='sm'
            onClick={async () => {
              await handleSkip()
            }}
            className='h-8 w-8 p-0'
          >
            <X className='w-4 h-4' />
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className='py-5'>
        <div className='w-full bg-gray-200 rounded-full h-2'>
          <div
            className='bg-yellow-500 h-2 rounded-full transition-all duration-300'
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Tip content */}
      <div className='pb-5'>
        <div className='mb-2'>
          <span className='inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded'>
            {currentTip?.topic}
          </span>
        </div>
        <h3 className='font-semibold text-lg mb-3'>{currentTip?.title}</h3>
        <div className='leading-relaxed text-sm'>
          {currentTip?.description ? (
            <div dangerouslySetInnerHTML={{ __html: convertJSXToHTML(currentTip.description) }} />
          ) : null}
        </div>
      </div>

      {/* Actions */}
      <div className='pt-5 border-t flex flex-wrap gap-2 items-center justify-between'>
        <div className='flex flex-wrap gap-2 '>
          <Button variant='outline' onClick={handleRemindLater} className='flex items-center gap-2'>
            <Clock className='w-4 h-4' />
            Remind in 24h
          </Button>
          <Button variant='outline' onClick={handleSkip} className='flex items-center gap-2'>
            <SkipForward className='w-4 h-4' />
            Skip All
          </Button>
        </div>

        <div className='flex flex-wrap gap-2 '>
          {currentTipIndex > 0 && (
            <Button variant='outline' onClick={handlePreviousTip}>
              Previous
            </Button>
          )}
          <Button
            onClick={async () => {
              await handleNextTip()
            }}
          >
            {currentTipIndex === tips.length - 1 ? 'Finish' : 'Next Tip'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
